import React, { useState, useRef, useEffect, useCallback } from "react";
import styles from "./GallerySlider.module.scss";

interface GallerySliderProps {
  images: string[];
}

const GallerySlider: React.FC<GallerySliderProps> = ({ images }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [direction, setDirection] = useState<"left" | "right">("right");
  const [thumbnails, setThumbnails] = useState<string[]>([]);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);

  const handleNext = () => {
    if (images.length <= 1) return;
    setDirection("right");
    setCurrentIndex((prevIndex) => (prevIndex + 1) % images.length);
  };

  const handlePrev = () => {
    if (images.length <= 1) return;
    setDirection("left");
    setCurrentIndex(
      (prevIndex) => (prevIndex - 1 + images.length) % images.length,
    );
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    if (images.length <= 1) return;
    touchStartX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (images.length <= 1) return;
    touchEndX.current = e.changedTouches[0].clientX;
    handleSwipe();
  };

  const handleSwipe = () => {
    if (images.length <= 1) return;
    if (touchStartX.current - touchEndX.current > 50) {
      handleNext();
    }

    if (touchStartX.current - touchEndX.current < -50) {
      handlePrev();
    }
  };

  const getThumbnails = useCallback(() => {
    if (images.length <= 4) {
      return images;
    }
    const start = currentIndex;
    const end = (currentIndex + 4) % images.length;
    if (end > start) {
      return images.slice(start, end);
    } else {
      return [...images.slice(start), ...images.slice(0, end)];
    }
  }, [currentIndex, images]);

  useEffect(() => {
    setThumbnails(getThumbnails());
  }, [getThumbnails]);

  return (
    <div
      className={styles.gallerySlider}
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      <div className={styles.mainImageWrapper}>
        {thumbnails.length > 1 && (
          <button className={styles.arrow} onClick={handlePrev}>
            {"<"}
          </button>
        )}
        <div className={styles.imageContainer}>
          <div
            className={`${styles.slide} ${
              direction === "right" ? styles.slideRight : styles.slideLeft
            }`}
            key={currentIndex} // ключ помогает React обновлять анимацию
          >
            <img
              className={styles.mainImage}
              src={images[currentIndex]}
              alt={`Slide ${currentIndex + 1}`}
            />
          </div>
        </div>
        {thumbnails.length > 1 && (
          <button className={styles.arrow} onClick={handleNext}>
            {">"}
          </button>
        )}
      </div>
      {thumbnails.length > 1 && (
        <div className={styles.thumbnails}>
          {thumbnails.map((image, index) => (
            <img
              key={index}
              className={`${styles.thumbnail} ${
                index === currentIndex ? styles.active : ""
              }`}
              src={image}
              alt={`Thumbnail ${index + 1}`}
              onClick={() => setCurrentIndex(index)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default GallerySlider;
